import {
  FocusableSection,
  bachEnableSections,
  disableAllSectionsExcept,
  focusSectionWithPath,
  useSpatialNavigation,
} from "@blacknut/spatialnav-sdk/dist";
import clsx from "clsx";
import React, { useCallback, useEffect, useRef } from "react";
import ReactModal from "react-modal";
import { CustomBackSubscription, useCustomBack } from "../../../utils";
import styles from "./styles.module.scss";

export interface ModalBaseProps extends ReactModal.Props {
  onClose?: () => void;
  testID?: string;
  focusableSectionClassName?: string;
  contentClassName?: string;
  children?: React.ReactNode;
  defaultFocus?: string;
}
const ModalBase = ({
  isOpen,
  onClose,
  onAfterOpen,
  testID,
  className,
  children,
  style,
  focusableSectionClassName,
  defaultFocus,
}: ModalBaseProps) => {
  const disabledSections = useRef([] as string[]);
  const { active: spatialNavigationActive, resume } = useSpatialNavigation();
  const { push: backPush } = useCustomBack();
  const backSubscription = useRef<CustomBackSubscription>();
  const historyUnregisterCallback = useRef<() => void>();
  useEffect(() => {
    if (isOpen && onClose) {
      backSubscription.current = backPush(() => {
        onClose();
        return true;
      });
    }
    return () => {
      backSubscription.current?.remove();
      backSubscription.current = undefined;
    };
  }, [backPush, isOpen, onClose]);

  useEffect(() => {
    return () => {
      if (historyUnregisterCallback.current) {
        historyUnregisterCallback.current();
        historyUnregisterCallback.current = undefined;
      }
    };
  }, []);

  const _onAfterOpen = useCallback(() => {
    resume();
    //Disable all focusable sections except the current one
    disabledSections.current = disableAllSectionsExcept("/modal");
    if (spatialNavigationActive) {
      focusSectionWithPath("/modal");
    }

    if (onAfterOpen) {
      onAfterOpen();
    }
  }, [spatialNavigationActive, onAfterOpen, resume]);

  const _onAfterClose = useCallback(() => {
    bachEnableSections(disabledSections.current);

    //Wait till app.router disable spatial nav to reenable it
    setTimeout(() => {
      resume();
    }, 200);
  }, [resume]);

  useEffect(() => {
    if (spatialNavigationActive) {
      focusSectionWithPath("/modal");
    }
  }, [spatialNavigationActive]);

  const _onRequestClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  return (
    <ReactModal
      testId={testID}
      isOpen={isOpen}
      onAfterOpen={_onAfterOpen}
      className={clsx(styles.modal, className)}
      onRequestClose={_onRequestClose}
      onAfterClose={_onAfterClose}
      style={{
        overlay: {
          position: "fixed",
          zIndex: 1002,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "#19141450",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          ...style?.overlay,
        },
        content: {
          width: window.innerWidth >= 960 ? 680 : "95%",
          background: "var(--modal-background, #F2F2F2)",
          position: "initial",
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
          borderRadius: "6px",
          outline: "none",
          padding: `3.2rem`,
          border: "none",
          borderTop: `5px solid var(--accent-color, #eb2553)`,
          color: "#000",
          boxShadow: "0 0 24px rgba(0,0,0,.3)",
          ...style?.content,
        },
      }}
    >
      <FocusableSection
        focusKey="modal"
        leaveFor={{ down: "" }}
        config={{ restrict: "self-only" }}
        className={focusableSectionClassName}
        {...(defaultFocus && { ...{ defaultElement: defaultFocus } })}
      >
        {children}
      </FocusableSection>
    </ReactModal>
  );
};
export default ModalBase;
export { ModalBase };
